import { memo, useCallback, useMemo, useRef } from 'react'
import { AlertOptions, closeAlert, shiftAlert, useAlertStore } from '@/ui/useAlertStore'
import { useCommon } from '@/core/useCommon'
import { AnimatePresence, type Variants, motion } from 'framer-motion'
import { cn } from '@/styles'
import { useTimeout } from 'ahooks'
import { useHideBodyScroll } from '@/core/useHideBodyScroll'
import { createPortal } from 'react-dom'
import { AlertButton } from './AlertButton'

const BgVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const ModalVariants: Variants = {
  initial: { opacity: 0, scale: 0.5, transition: { duration: 0.2 } },
  animate: { opacity: 1, scale: 1, transition: { ease: 'backOut', duration: 0.2 } },
  exit: { opacity: 0, scale: 0.5, transition: { ease: 'easeInOut', duration: 0.2 } },
}

/** Alert 樣式 */
const AlertDialog = (props: AlertOptions & { onExitComplete: () => void }) => {
  const { t } = useCommon()

  // 防連點
  const disabledEvent = useRef(false)
  // 關閉後，強制無法點擊
  disabledEvent.current = props.enabled === false ? true : disabledEvent.current

  /** 關閉 */
  const onClose = useCallback<Required<AlertOptions>['onClose']>(
    async (...args) => {
      if (disabledEvent.current) return
      try {
        disabledEvent.current = true
        await Promise.all([props?.onClose?.(...args)])
      } finally {
        disabledEvent.current = false
      }
    },
    [props],
  )

  /** 確認 */
  const onConfirm = useCallback<Required<AlertOptions>['onConfirm']>(
    async (...args) => {
      if (disabledEvent.current) return
      try {
        disabledEvent.current = true
        if (typeof props?.onConfirm === 'function') await Promise.all([props?.onConfirm?.(...args)])
        else await Promise.all([props?.onClose?.({})] as any)
      } finally {
        disabledEvent.current = false
      }
    },
    [props],
  )

  /** 底部選單 */
  const Footer = useMemo(() => {
    if (props.children) return null

    // 處理中
    const closeLabel = props?.close === false ? '' : props.close || t('old:cancel')
    const confirmLabel = props?.confirm === false ? '' : props.confirm || t('old:confirmButtonText')

    if (!closeLabel && !confirmLabel) return null
    return (
      <div className={cn('flex items-center justify-center space-x-16', props?.classNames?.footer)}>
        {/*取消*/}
        {closeLabel && (
          <AlertButton variant="outline" onClick={onClose} className="min-w-96" {...props?.closeProps}>
            {closeLabel}
          </AlertButton>
        )}
        {/*確認*/}
        {confirmLabel && (
          <AlertButton onClick={onConfirm} className="min-w-96" {...props?.confirmProps}>
            {confirmLabel}
          </AlertButton>
        )}
      </div>
    )
  }, [props, t, onClose, onConfirm])

  /** 客製或公版 */
  const Content = useMemo(() => {
    if (props.children) return <>{props.children?.({ onClose, onConfirm })}</>
    const title = typeof props.title === 'string' ? <h1 className={cn('text-16 text-center font-bold', props.classNames?.title)}>{props.title}</h1> : props.title || null
    const message = typeof props.message === 'string' ? <p className={cn('text-14 break-words', props.classNames?.message)}>{props.message}</p> : props.message || null
    return (
      <div
        className={cn('rounded-8 relative flex min-w-220 flex-col items-center justify-center gap-16 border border-[#F0F0F01A] bg-white p-16 text-[#181E2A]', props.classNames?.content)}
        onClick={(e) => e.stopPropagation()}>
        {/*標題*/}
        {title}
        {/*訊息*/}
        {message}
        {/*右上角浮動叉叉*/}
        {props.closeButton && (
          <button onClick={() => onClose({ isOnlyClose: true })} className="color-foreground absolute top-0 right-0 z-10 flex h-40 w-40 items-center justify-center">
            <svg className="h-20 w-20" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
                fill="currentColor"
                fillRule="evenodd"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
        {/*底部選單*/}
        {Footer}
      </div>
    )
  }, [props])

  /** 背景關閉 */
  const onBgClose = () => {
    if (disabledEvent.current) return
    closeAlert()
  }

  /** 防呆：如果這裡觸發，代表 onExitComplete 失靈 */
  useTimeout(props?.onExitComplete, props?.enabled ? undefined : 500)

  /** 隱藏捲軸 */
  useHideBodyScroll()

  return createPortal(
    <AnimatePresence onExitComplete={props?.onExitComplete}>
      {/*背景*/}
      {props?.enabled && (
        <motion.div key="bg" variants={BgVariants} initial="initial" exit="initial" animate="animate" className={cn('fixed top-0 left-0 z-200 h-full w-full bg-black/70', props.classNames?.bg)} />
      )}
      {/*彈窗*/}
      {props.enabled && (
        <motion.div
          key="alert"
          variants={ModalVariants}
          initial="initial"
          exit="initial"
          animate="animate"
          className={cn('fixed top-0 left-0 z-200 flex h-full w-full items-center justify-center', props.classNames?.body)}
          onClick={props?.bgClose ? onBgClose : undefined}>
          {Content}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

/** 全域 Alert 管理 */
export const AlertsManager = memo(() => {
  // 彈窗訊息
  const alerts = useAlertStore((state) => state?.alerts)
  if (!alerts?.length) return null
  return alerts.map(({ key, ...alert }) => <AlertDialog key={key} {...alert} onExitComplete={() => shiftAlert(key)} />)
})

AlertsManager.displayName = 'AlertsManager'
